import React, { useState, useEffect, useContext } from 'react'
import {
  Row,
  Col,
  Form,
  Badge,
  Tooltip,
  OverlayTrigger,
  Table,
  ButtonGroup,
  ToggleButton
} from 'react-bootstrap'
import { Formik, FieldArray } from 'formik'
import * as yup from 'yup'
import TablePayments from './tables/tablePayments'
import { Listing } from '../../../config/enums'
import { toast } from 'react-toastify'
import { friendlyMessageFor } from '../../../utility/errorHelper'
import { Redirect } from 'react-router'
import ImageContainer from './images/imageContainer'
import ImageUpload from './images/imageUpload'
import constants from '../../../config/constants'
import { FaPlus } from 'react-icons/fa'
import { VendorContext } from '../../../contexts/vendorContext'
import { getCategories } from '../../../services/genericService'
import { Category } from '../../../models/Category'
import TableDelivery from './tables/tableDelivery'
import TableVariants from './tables/tableVariants'
import {
  createItem,
  deleteItem,
  updateItem
} from '../../../services/vendorService'
import {
  DeliveryOption,
  Installment,
  Item,
  Variant
} from '../../../models/Item'
import { GiftBundle } from '../../../models/GiftBundle'
import styles from './listingForm.module.css'
import AddListingButton from './items/addButtonGroup'
import EditListingButtons from './items/editButtonGroup'
import Confirm from '../../common/modals/confirm'
import { getGiftBundles } from '../../../services/giftService'
import { generateStraightPaymentObjects } from '../../../utility/installmentHelper'

type Props = {
  isEditing: boolean
  initialValues: Item
  isInstallmentPayment?: boolean
  id?: string
  handleConfirm?: () => void
}

const ManageListingForm = ({
  isEditing,
  initialValues,
  id,
  handleConfirm,
  isInstallmentPayment
}: Props) => {
  const [redirectToListing, setRedirectToListing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [categories, setCategories] = useState<Category[]>([])
  const [giftBundles, setGiftBundles] = useState<GiftBundle[]>([])
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [country, setCountry] = useState('')
  const [isInstallment, setIsInstallment] = useState(
    isInstallmentPayment ? true : false
  )

  const vendorContext = useContext(VendorContext)
  const vendorId = vendorContext?.profile?.vendorId

  /** Generic Functions */
  const fetchAllCategories = async () => {
    try {
      setCategories(await getCategories())
    } catch (error) {
      toast.error(constants.alertError.fetchingCategoriesError, {
        autoClose: false
      })
    }
  }

  const fetchAllGiftBundles = async () => {
    if (vendorId) {
      setGiftBundles(await getGiftBundles(vendorId))
    } else {
      toast.error(constants.alertError.noVendorId, { autoClose: false })
    }
  }

  const switchConfirmationModal = () => {
    if (showConfirmModal) {
      setShowConfirmModal(false)
    } else {
      setShowConfirmModal(true)
    }
  }

  const getOriginCountry = async () => {
    const country = vendorContext?.companyProfile?.originCountry?.name
    if (country !== undefined) {
      setCountry(country)
    } else {
      setCountry('')
    }
  }

  const schema = yup.object({
    categoryIds: yup
      .array()
      .required(constants.inlineError.fieldRequired.replace('%s', 'Category')),
    title: yup
      .string()
      .required(
        constants.inlineError.fieldRequired.replace('%s', 'Product name')
      ),
    description: yup
      .string()
      .required(
        constants.inlineError.fieldRequired.replace('%s', 'Product description')
      ),
    isActive: yup.bool().required(),
    giftBundleId: yup.string().nullable(true),
    installmentTerms: yup.array().of(
      yup.object().shape({
        month3: yup.number(),
        month6: yup.number()
      })
    ),
    deliveryOptions: yup.array().of(
      yup.object().shape({
        region: yup
          .string()
          .required(
            constants.inlineError.fieldRequired.replace('%s', 'Region')
          ),
        courier: yup
          .string()
          .required(
            constants.inlineError.fieldRequired.replace('%s', 'Courier')
          ),
        minDays: yup
          .number()
          .required(
            constants.inlineError.fieldRequired.replace('%s', 'Min days')
          ),
        maxDays: yup
          .number()
          .required(
            constants.inlineError.fieldRequired.replace('%s', 'Max days')
          ),
        fee: yup
          .number()
          .required(constants.inlineError.fieldRequired.replace('%s', 'Fee'))
      })
    ),
    variants: yup.array().of(
      yup.object().shape({
        price: yup
          .number()
          .moreThan(
            0,
            constants.inlineError.listingForm.lessThanZero.replace(
              '%s',
              'Price'
            )
          )
          .required(
            constants.inlineError.fieldRequired.replace('%s', 'Variant Price')
          ),
        description: yup
          .string()
          .required(
            constants.inlineError.fieldRequired.replace(
              '%s',
              'Variant Description'
            )
          )
      })
    ),
    images: yup
      .array()
      .of(yup.string())
      .required(constants.inlineError.fieldRequired.replace('%s', 'Image'))
  })

  useEffect(() => {
    fetchAllCategories()
    fetchAllGiftBundles()
    getOriginCountry()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (redirectToListing) return <Redirect to="/vendor/listings" />

  return (
    <Formik
      data-testid="edit-form"
      validationSchema={schema}
      onSubmit={values => {
        console.log(values)
        setIsLoading(true)
        if (isEditing && id) {
          toast.info(constants.alertMessage.updatingListingItem)
          updateItem(
            id,
            values.title,
            values.description,
            values.variants as Variant[],
            isInstallment
              ? (values.installmentTerms as Installment[])
              : generateStraightPaymentObjects(values.variants.length),
            values.isActive,
            values.deliveryOptions as DeliveryOption[],
            values.categoryIds,
            values.images as string[],
            values.giftBundleId as string
          ).then(itemId => {
            toast.dismiss()
            setIsLoading(false)
            setRedirectToListing(true)
            toast.success(
              constants.alertMessage.updatingListingItemSuccessful,
              { autoClose: 3000 }
            )
          })
        } else {
          toast.info(constants.alertMessage.addingListingItem)
          createItem(
            values.title,
            values.description,
            values.variants as Variant[],
            isInstallment
              ? (values.installmentTerms as Installment[])
              : generateStraightPaymentObjects(values.variants.length),
            values.isActive,
            values.deliveryOptions as DeliveryOption[],
            values.categoryIds,
            values.images as string[],
            values.giftBundleId as string
          )
            .then(itemId => {
              toast.dismiss()
              setIsLoading(false)
              setRedirectToListing(true)
              toast.success(
                constants.alertMessage.addingListingItemSuccessful,
                {
                  autoClose: 3000
                }
              )
            })
            .catch(error => {
              toast.dismiss()
              console.log(error)
              toast.warn(friendlyMessageFor(error.code))
            })
        }
      }}
      initialValues={{
        categoryIds: initialValues.categoryIds,
        variants: initialValues.variants,
        title: initialValues.title,
        description: initialValues.description,
        isActive: initialValues.isActive,
        giftBundleId: initialValues.giftBundleId,
        installmentTerms: initialValues.installmentOptions as Installment[],
        deliveryOptions: initialValues.deliveryOptions,
        images: initialValues.images as string[]
      }}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        errors,
        touched,
        setFieldValue
      }) => (
        <Form
          onSubmit={handleSubmit}
          noValidate={true}
          data-testid="listing-form"
          className="pb-5"
        >
          <Confirm
            show={showConfirmModal}
            body={'Are you sure you want to delete ' + values.title + '?'}
            confirmText="Ok"
            loadingText="Deleting.."
            onConfirmClick={() => {
              if (id) {
                setIsDeleting(true)
                deleteItem(id)
                  .then(() => {
                    setIsDeleting(false)
                    switchConfirmationModal()
                    setRedirectToListing(true)
                  })
                  .catch(error => {
                    setIsDeleting(false)
                    console.error(error)
                    toast.error(friendlyMessageFor(error), { autoClose: false })
                  })
              }
            }}
            onCancelClick={switchConfirmationModal}
            isLoading={isDeleting}
          />

          <Row id="listing-photos">
            <Col>
              <h3>Photos</h3>
              <FieldArray name="images">
                {({ push, remove }) => (
                  <>
                    <ImageUpload
                      onImageUpload={push}
                      images={values.images as string[]}
                      handleError={errors}
                      handleTouch={touched}
                    />
                    <Row xs={12} className="pt-3">
                      {values.images.length > 0 ? (
                        values.images.map((imageUrl, index) => {
                          return (
                            <Col xs={12} sm={4} key={index}>
                              <ImageContainer
                                id={id}
                                imageSrc={imageUrl}
                                imageArray={values.images}
                                onImageDelete={remove}
                                onImageTouched={handleConfirm}
                              />
                            </Col>
                          )
                        })
                      ) : (
                        <>
                          <Col xs={6} sm={4}>
                            <ImageContainer />
                          </Col>
                          <Col xs={6} sm={4}>
                            <ImageContainer />
                          </Col>
                          <Col xs={6} sm={4}>
                            <ImageContainer />
                          </Col>
                        </>
                      )}
                    </Row>
                  </>
                )}
              </FieldArray>
            </Col>
          </Row>

          <Row id="listing-dropdown-group" className="pt-5">
            <Col lg={6}>
              <Form.Group>
                <Form.Label>Product</Form.Label>
                <Form.Control
                  type="text"
                  onChange={handleChange}
                  name="title"
                  isInvalid={touched.title && !!errors.title}
                  value={values.title}
                  placeholder="type product name"
                />

                <Form.Control.Feedback type="invalid">
                  {errors.title}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group>
                <Form.Label>Category</Form.Label>

                {/**
                 * ? Added index zero and set multiple to false.
                 * This is to remove the error The `value` prop
                 * supplied to <select> must be a scalar value
                 * console error.
                 * */}

                <Form.Control
                  as="select"
                  isInvalid={
                    touched.categoryIds && values.categoryIds.length === 0
                  }
                  onChange={(e: React.FormEvent<HTMLSelectElement>) => {
                    setFieldValue('categoryIds', [e.currentTarget.value])
                  }}
                  value={values.categoryIds[0]}
                >
                  {!isEditing && <option value=""> </option>}
                  {categories.map(item => {
                    return (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    )
                  })}
                </Form.Control>
                <Form.Control.Feedback type="invalid">
                  {errors.categoryIds}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col lg={6}>
              <Form.Group>
                <Form.Label>Listing Status</Form.Label>
                <br />
                <Form.Check
                  type="switch"
                  id="listing-status"
                  data-testid="listing-status"
                  label={values.isActive ? Listing.enabled : Listing.disabled}
                  className={'pl-4 ml-3 ' + styles.labelSwitch}
                  checked={values.isActive}
                  onChange={() => {
                    if (values.isActive) {
                      setFieldValue('isActive', false)
                    } else {
                      setFieldValue('isActive', true)
                    }
                  }}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label className="pt-2 mt-1">Gift Bundle #</Form.Label>
                <Form.Control
                  as="select"
                  value={values.giftBundleId || ''}
                  onChange={handleChange}
                  name="giftBundleId"
                >
                  <option value="">None</option>
                  {giftBundles.map(item => {
                    return (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    )
                  })}
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>
          <Row id="listing-description">
            <Col>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows="5"
                  onChange={handleChange}
                  name="description"
                  isInvalid={touched.description && !!errors.description}
                  value={values.description}
                  placeholder="type product description"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.description}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row id="listing-add-variants">
            <Col className="pt-5">
              <Row>
                <Col className="align-v-center">
                  <h2 className="text-dark">
                    <b>Add Variants</b>
                  </h2>
                  <span className="pl-2">
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Tooltip id="tooltip-variant">
                          <p>TOTAL PRICE = variant price * base price</p>
                        </Tooltip>
                      }
                    >
                      <Badge pill variant="primary">
                        {' '}
                        ?{' '}
                      </Badge>
                    </OverlayTrigger>
                  </span>
                </Col>
              </Row>
              <Row>
                <Col lg={12} xl={8}>
                  <FieldArray name="variants">
                    {({ push, remove }) => (
                      <Table>
                        <thead
                          className={
                            'text-center text-primary ' +
                            styles.listingFormTableHeader
                          }
                        >
                          <tr>
                            <th className="text-center">Price</th>
                            <th className="text-center">Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          <TableVariants
                            variants={values.variants}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            handleTouch={touched}
                            handleError={errors}
                            handleRemove={remove}
                          />
                          <tr>
                            <td colSpan={3}>
                              <FaPlus
                                className="mt-3"
                                onClick={() => {
                                  push({
                                    price: 0,
                                    description: ' '
                                  })

                                  values.installmentTerms.push({
                                    month3: 0,
                                    month6: 0
                                  })
                                }}
                              />
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    )}
                  </FieldArray>
                </Col>
              </Row>
            </Col>
          </Row>

          <Row id="listing-add-payment">
            <Col className="pt-2">
              <Row id="listing-delivery">
                <Col className="pt-5">
                  <Row>
                    <Col className="align-v-center">
                      <h2 className="text-dark">
                        <b>Delivery Options</b>
                      </h2>
                      <span className="pl-2">
                        <OverlayTrigger
                          placement="right"
                          overlay={
                            <Tooltip id="tooltip-payment">
                              <p>Delivery not calculated in total price</p>
                            </Tooltip>
                          }
                        >
                          <Badge pill variant="primary">
                            {' '}
                            ?{' '}
                          </Badge>
                        </OverlayTrigger>
                      </span>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FieldArray name="deliveryOptions">
                        {({ push, remove }) => (
                          <Table>
                            <thead
                              className={
                                'text-center text-primary ' +
                                styles.listingFormTableHeader
                              }
                            >
                              <tr>
                                <th
                                  style={{ width: '30%' }}
                                  className="text-center"
                                >
                                  Region
                                </th>
                                <th className="text-center">Delivery Fee</th>
                                <th colSpan={2} className="text-center">
                                  Delivery Time
                                </th>
                                <th
                                  style={{ width: '20%' }}
                                  className="text-center"
                                >
                                  Courier
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <TableDelivery
                                deliveryOptions={values.deliveryOptions}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                handleTouch={touched}
                                handleError={errors}
                                handleRemove={remove}
                                setRegionValue={setFieldValue}
                                country={country}
                              />
                              <tr>
                                <td colSpan={6}>
                                  <FaPlus
                                    className="mt-3"
                                    onClick={() => {
                                      push({
                                        region: '',
                                        courier: '',
                                        minDays: 0,
                                        maxDays: 0,
                                        fee: 0
                                      })
                                    }}
                                  />
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        )}
                      </FieldArray>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Row className="pt-3">
                <Col lg={12} xl={12}>
                  <h3>Payment Terms</h3>
                  <ButtonGroup toggle>
                    <ToggleButton
                      type="radio"
                      variant="secondary"
                      name="radio"
                      value={isInstallment}
                      checked={!isInstallment}
                      className={
                        !isInstallment
                          ? styles.installmentSelect
                          : styles.installmentDeselect
                      }
                      onChange={() => {
                        setIsInstallment(false)
                      }}
                    >
                      Cash price
                    </ToggleButton>
                    <ToggleButton
                      type="radio"
                      variant="secondary"
                      name="radio"
                      value={isInstallment}
                      checked={isInstallment}
                      className={
                        isInstallment
                          ? styles.installmentSelect
                          : styles.installmentDeselect
                      }
                      onChange={() => {
                        setIsInstallment(true)
                      }}
                    >
                      Installment
                    </ToggleButton>
                  </ButtonGroup>
                  {isInstallment && (
                    <FieldArray name="installmentTerms">
                      {({ push, remove }) => (
                        <Table>
                          <thead
                            className={
                              'text-center text-primary ' +
                              styles.listingFormTableHeader
                            }
                          >
                            <tr>
                              <th className="text-center"></th>
                              <th className="text-center">3 months</th>
                              <th className="text-center">6 months</th>
                            </tr>
                          </thead>
                          <tbody>
                            <TablePayments
                              installmentTerms={values.installmentTerms}
                              variants={values.variants}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              handleTouch={touched}
                              handleError={errors}
                              handleRemove={remove}
                            />
                          </tbody>
                        </Table>
                      )}
                    </FieldArray>
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
          <Row>
            {isEditing ? (
              <EditListingButtons
                isLoading={isLoading}
                onDelete={switchConfirmationModal}
              />
            ) : (
              <AddListingButton isLoading={isLoading} />
            )}
          </Row>
        </Form>
      )}
    </Formik>
  )
}

export default ManageListingForm
